
import { Component, Vue, Watch } from 'vue-property-decorator';
import CountTo from 'vue-count-to';

import IButton from '@/ui-components/IButton/IButton.vue';
import Stepper from '@/ui-components/Stepper/Stepper.vue';
import StepperContent from '@/ui-components/Stepper/StepperContent.vue';
import StepperItems from '@/ui-components/Stepper/StepperItems.vue';
import StepperStep from '@/ui-components/Stepper/StepperStep.vue';
import StepperHeader from '@/ui-components/Stepper/StepperHeader.vue';
import StepperHeaderResponsive from '@/ui-components/Stepper/StepperHeaderResponsive.vue';
import { Action, Getter } from 'vuex-class';
import namespaces from '@/store/namespaces';
import Whatsapp from '@/pages/shared/CampaignWizard/components/Whatsapp.vue';
import Locations from './components/Locations/Locations.vue';
import CampaignTime from './components/Time/Time.vue';
import AdContent from './components/AdContent/AdContent.vue';
import Account from './components/Account/Account.vue';
import Payment from './components/Payment/Payment.vue';
import PriceSummary from '@/pages/shared/CampaignWizard/components/PriceSummary.vue';
import MediaUploadGuidelines from '@/shared-components/MediaUploadGuidelines.vue';
import { LocationData, LocationPrice } from '@/types/locations';
import { steps } from '@/statics/campaign-wizard';
import {
  CheckoutCampaign,
  DeselectLocation,
  InitNewCampaign,
  UpdateStep, UpdateTime,
} from '@/store/modules/campaign-wizard/types';
import { TimeSlotSelection } from '@/types/time-slots';
import { ShowSnackbar } from '@/types/snackbar';
import RightPaymentSection from '@/pages/shared/CampaignWizard/components/Payment/components/RightPaymentSection.vue';
import { isRouterReady } from '@/utils/router';
import { publicRouteNames } from '@/route-names/public';
import { AuthorizedUser } from "@/types/users";
import { redirectUserToAuthorizedRoleDashboard } from "@/utils/user";
import { getLocationPriceByFrequency } from '@/utils/locations';
import { toNumberOr } from '@/utils/numbers';
import { Discount } from '@/types/promotions';

@Component({
  components: {
    RightPaymentSection,
    Stepper,
    StepperContent,
    StepperItems,
    StepperStep,
    StepperHeader,
    Account,
    Whatsapp,
    Locations,
    CampaignTime,
    IButton,
    AdContent,
    Payment,
    StepperHeaderResponsive,
    PriceSummary,
    MediaUploadGuidelines,
    CountTo,
  },
  beforeRouteEnter(to, from, next){
    next((vm: any)=>{
      if (from.name === publicRouteNames.RESET_PASSWORD.name) {
        vm.isFromResetPasswordPage = true;
      }
    });
  },
})
export default class CampaignWizard extends Vue {
  @Getter('step', { namespace: namespaces.CampaignWizardModule })
  public currentStep!: number;
  public loading = true;
  public discounts: Discount[] = [];

  @Getter('isAuthenticated', { namespace: namespaces.AuthModule })
  public isAuthenticated!: boolean;
  @Getter('userInfo', { namespace: namespaces.AuthModule })
  public userInfo!: AuthorizedUser;

  @Getter("selectedLocations", { namespace: namespaces.CampaignWizardModule })
  public selectedLocationsVuex!: LocationData[];

  @Getter('selectedLocations', { namespace: namespaces.CampaignWizardModule })
  public campaignWizardSelectedLocations!: LocationData[];

  @Getter('time', { namespace: namespaces.CampaignWizardModule })
  public campaignWizardSelectedTimeSlots!: TimeSlotSelection;

  @Action('updateStep', { namespace: namespaces.CampaignWizardModule })
  public updateStep!: UpdateStep;

  @Action('initNewCampaign', { namespace: namespaces.CampaignWizardModule })
  public initNewCampaign!: InitNewCampaign;
  @Action("deselectLocation", { namespace: namespaces.CampaignWizardModule })
  public deselectLocation!: DeselectLocation;
  @Action('checkoutCampaign', { namespace: namespaces.CampaignWizardModule })
  public checkoutCampaign!: CheckoutCampaign;
  @Action('showSnackbar', { namespace: namespaces.UiModule })
  public showSnackbar!: ShowSnackbar;
  @Action('updateTime', { namespace: namespaces.CampaignWizardModule })
  public updateTime!: UpdateTime;

  public selectedLocations: LocationData[] = [];
  public isPublic = true;
  public isFromResetPasswordPage = false;

  public locationPrices = {} as Record<LocationData['ID'],LocationPrice>;
  public totalPrices = 0;
  public get steps() {
    return steps.filter(
      ({ onlyPublic }) =>
        this.isPublic || (!onlyPublic || (this.isPublic && onlyPublic))
    );
  }

  public get paymentStepNumber() {
    return this.isPublic ? 5 : 4;
  }
  updatePrice(){
    const {
      frequency
    } = this.campaignWizardSelectedTimeSlots;
    this.locationPrices = this.campaignWizardSelectedLocations.reduce((selectedLocations,selectedLocation:LocationData)=>{
      return { ...selectedLocations,
        [selectedLocation.clusterId]: {
          BASE_PRICE: getLocationPriceByFrequency(selectedLocation, frequency)
        }
      }
    },{});
    const {duration} = this.campaignWizardSelectedTimeSlots;
    const totalDuration = toNumberOr(duration,0)
    this.totalPrices = Object.values(this.locationPrices).reduce((totalPrice, locationPrice) => {
          totalPrice += locationPrice.BASE_PRICE * totalDuration;
          return totalPrice;
        }
        , 0)
  }
  public initWizard() {
    this.initNewCampaign();
    this.isPublic = !this.isAuthenticated || Boolean(this.isFromResetPasswordPage);
    this.updatePrice();
  }

  public async created() {
    await isRouterReady(this.$router);
    await redirectUserToAuthorizedRoleDashboard(this.$router,this.userInfo);
    this.initWizard();
    this.initNewCampaign();
  }

  public previousStep() {
    this.updateStep(this.currentStep - 1);
  }

  public nextStep() {
    this.updateStep(this.currentStep + 1);
  }

  public goToStep(step: number) {
    this.updateStep(step);
  }
  @Watch('campaignWizardSelectedTimeSlots', { deep: true })
  updatePriceOnFrequencyChanged(){
    this.updatePrice()
  }
  @Watch('campaignWizardSelectedLocations', { deep: true })
  updatePriceOnSelectedLocationChanged(){
    this.updatePrice()
  }

  @Watch('isAuthenticated', { deep: true })
  async authenticationChange(val: boolean, prevVal: boolean) {
    const hasUserLoggedOut = !val && prevVal;

    if (hasUserLoggedOut) {
      location.reload();
    }
  }
}
